import { FC, useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import Header from "../layouts/header";
import Footer from "../layouts/footer";
import { getInvoiceById, UpdateInvoiceById } from "../../redux/actions/invoiceAction";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { errorMsgs } from "../../context/notify";
import { formatDate } from "../../config/datetimeformat";
import ImageModalView from '../../components/ImageModal';

interface InvoiceFormValues {
  invoiceid: number;
  id: string;
  name: string;
  latitude: string;
  longitude: string;
  state: string;
  username: string;
  timestamp: string;
  task_date: string;
  task_name_of_input: string;
  task_total_cost_of_the_receipt: number;
  task_only_for_enrolled_fields: number;
  task_task_end_timestamp: string;
  task_survey_version: number;
  image_file_name: string;
  import_date: string;
  status: string;
  downloaded: boolean;
}

const InvoiceEdit: FC = () => {
  const { invoiceid } = useParams<{ invoiceid: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState<InvoiceFormValues>({
    invoiceid: 0,
    id: "",
    name: "",
    latitude: "",
    longitude: "",
    state: "",
    username: "",
    timestamp: "",
    task_date: "",
    task_name_of_input: "",
    task_total_cost_of_the_receipt: 0,
    task_only_for_enrolled_fields: 0,
    task_task_end_timestamp: "",
    task_survey_version: 0,
    image_file_name: "",
    import_date: "",
    status: "",
    downloaded: false
  });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    if (invoiceid) {
      getInvoiceData();
    }
  }, [invoiceid]);

  const getInvoiceData = async () => {
    const payload = { invoiceid: parseInt(invoiceid!, 10) };
    dispatch(
      getInvoiceById(payload, (response: any) => {
        if (response?.data) {
          setInitialValues({
            ...response.data,
            timestamp: response.data.timestamp,
            task_date: response.data.task_date,
            task_task_end_timestamp: response.data.task_task_end_timestamp,
            import_date: response.data.import_date
          });
          setImageUrl(response.data.fileItem?.webUrl || '');
        }
      })
    );
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    latitude: Yup.string().required("Latitude is required"),
    longitude: Yup.string().required("Longitude is required"),
    state: Yup.string().required("State is required"),
    username: Yup.string().required("Username is required"),
    task_name_of_input: Yup.string().required("Name of input is required"),
    task_total_cost_of_the_receipt: Yup.number().required("Total cost is required"),
    task_survey_version: Yup.number().required("Survey version is required")
  });

  const formik: FormikProps<InvoiceFormValues> = useFormik<InvoiceFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      dispatch(
        UpdateInvoiceById(values, (response: any) => {
          try {
            if (response.statusCode === 0) {
              errorMsgs(response.message);
            } else if (response.statusCode === 1) {
              navigate("/Invoice");
            } else if (response.statusCode === 2) {
              navigate("/Invoice");
            }
          } catch (error) {
            console.error("Error updating invoice:", error);
          } finally {
            setSubmitting(false);
          }
        })
      );
    },
  });

  return (
    <div>
      <Header />
      <div className="midCon pb-2">
        <section className="pt-4 pb-4 pt-md-5 pb-md-5">
          <div className="container">
            <div className="loginHd mb-1">
              <span className="d-block">Edit Invoice</span>
              Invoice Id: {invoiceid}
            </div>
          </div>
        </section>
        <form onSubmit={formik.handleSubmit}>
          <section className="pt-4 pb-4 pt-md-5 pb-md-5 mt-2 whtBg">
            <div className="container">
              <ul className="d-flex flex-wrap p-0 viewList">
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Name</span>
                    <input
                      type="text"
                      className="loginInput"
                      {...formik.getFieldProps('name')}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="error">{formik.errors.name}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Latitude</span>
                    <input
                      type="text"
                      className="loginInput"
                      {...formik.getFieldProps('latitude')}
                    />
                    {formik.touched.latitude && formik.errors.latitude && (
                      <div className="error">{formik.errors.latitude}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Longitude</span>
                    <input 
                      type="number" 
                      className="loginInput" 
                      {...formik.getFieldProps('longitude')}
                    />
                    {formik.touched.longitude && formik.errors.longitude && (
                      <div className="error">{formik.errors.longitude}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">State</span>
                    <input 
                      type="text" 
                      className="loginInput" 
                      {...formik.getFieldProps('state')}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <div className="error">{formik.errors.state}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Username</span>
                    <input 
                      type="text" 
                      className="loginInput" 
                      {...formik.getFieldProps('username')}
                    />
                    {formik.touched.username && formik.errors.username && (
                      <div className="error">{formik.errors.username}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Timestamp</span>
                    <input 
                      type="datetime-local" 
                      className="loginInput" 
                      {...formik.getFieldProps('timestamp')}
                    />
                    {formik.touched.timestamp && formik.errors.timestamp && (
                      <div className="error">{formik.errors.timestamp}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Task Date</span>
                    <input 
                      type="datetime-local" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_date')}
                    />
                    {formik.touched.task_date && formik.errors.task_date && (
                      <div className="error">{formik.errors.task_date}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Name of Input</span>
                    <input 
                      type="text" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_name_of_input')}
                    />
                    {formik.touched.task_name_of_input && formik.errors.task_name_of_input && (
                      <div className="error">{formik.errors.task_name_of_input}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Total Cost of Receipt</span>
                    <input 
                      type="text" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_total_cost_of_the_receipt')}
                    />
                    {formik.touched.task_total_cost_of_the_receipt && formik.errors.task_total_cost_of_the_receipt && (
                      <div className="error">{formik.errors.task_total_cost_of_the_receipt}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Only for Enrolled Fields</span>
                    <input 
                      type="text" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_only_for_enrolled_fields')}
                    />
                    {formik.touched.task_only_for_enrolled_fields && formik.errors.task_only_for_enrolled_fields && (
                      <div className="error">{formik.errors.task_only_for_enrolled_fields}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Task End Timestamp</span>
                    <input 
                      type="datetime-local" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_task_end_timestamp')}
                    />
                    {formik.touched.task_task_end_timestamp && formik.errors.task_task_end_timestamp && (
                      <div className="error">{formik.errors.task_task_end_timestamp}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Import Date</span>
                    <input 
                      type="datetime-local" 
                      className="loginInput" 
                      {...formik.getFieldProps('import_date')}
                    />
                    {formik.touched.import_date && formik.errors.import_date && (
                      <div className="error">{formik.errors.import_date}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Status</span>
                    <select 
                      className="loginInput" 
                      {...formik.getFieldProps('status')}
                    >
                      <option value="">Select Status</option>
                      <option value="NOT_APPROVED">NOT APPROVED</option>
                      <option value="APPROVED">APPROVED</option>
                    </select>
                    {formik.touched.status && formik.errors.status && (
                      <div className="error">{formik.errors.status}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Downloaded</span>
                    <select 
                      className="loginInput"
                      {...formik.getFieldProps('downloaded')}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    {formik.touched.downloaded && formik.errors.downloaded && (
                      <div className="error">{formik.errors.downloaded}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Survey Version</span>
                    <input 
                      type="number" 
                      className="loginInput" 
                      {...formik.getFieldProps('task_survey_version')}
                    />
                    {formik.touched.task_survey_version && formik.errors.task_survey_version && (
                      <div className="error">{formik.errors.task_survey_version}</div>
                    )}
                  </div>
                </li>
                <li className="col-12 col-sm-6">
                  <div className="position-relative">
                    <span className="field">Image File Name</span>
                    <div className="d-flex align-items-center">
                      <input 
                        type="text" 
                        className="loginInput" 
                        {...formik.getFieldProps('image_file_name')}
                      />
                      <button
                        type="button"
                        className="ViewButton ms-2"
                        onClick={() => setIsImageModalOpen(true)}
                      >
                        <span className="material-icons-outlined">visibility</span>
                      </button>
                    </div>
                    {formik.touched.image_file_name && formik.errors.image_file_name && (
                      <div className="error">{formik.errors.image_file_name}</div>
                    )}
                  </div>
                </li>
              </ul>
              <div className="pt-5 text-end">
                <NavLink to="/Invoice/view">
                  <button type="button" className="formBtn cancel">
                    Cancel
                  </button>
                </NavLink>
                <button 
                  type="submit" 
                  className="ms-3 formBtn"
                  disabled={formik.isSubmitting}
                >
                  Save Details
                </button>
              </div>
            </div>
          </section>
        </form>
      </div>
      <ImageModalView
        isOpen={isImageModalOpen}
        imageUrl={imageUrl}
        onClose={() => setIsImageModalOpen(false)}
      />
      <Footer />
    </div>
  );
};

export default InvoiceEdit;
